<template>
  <tr class="ing-row" :class="{ 'in-stock': !needsPurchase }">
    <td class="po-checkbox-col">
      <input
        type="checkbox"
        :checked="isSelected"
        @change="$emit('toggle-select', $event.target.checked)"
      />
    </td>

    <td>
      <div class="is-flex is-align-items-center gap-2">
        <span class="has-text-weight-bold" data-ingredient-name>{{ ingredient.name }}</span>
        <span v-if="needsPurchase" class="tag is-success is-light is-small">To Purchase</span>
        <span v-else class="tag is-small">In stock</span>
      </div>
    </td>

    <td class="no-wrap">
      {{ valueInUnit(ingredient.required) }}&thinsp;<span class="has-text-grey is-size-7">{{
        draft.unit
      }}</span>
    </td>

    <td class="no-wrap" data-onhand>
      {{ valueInUnit(ingredient.on_hand) }}&thinsp;<span class="has-text-grey is-size-7">{{
        draft.unit
      }}</span>
    </td>

    <td class="no-wrap" data-onorder>
      <template v-if="ingredient.on_order > 0 || draftQtyInBaseUnit > 0 || stagedQtyBase > 0">
        <template v-if="ingredient.on_order > 0">
          {{ valueInUnit(ingredient.on_order) }}&thinsp;<span class="has-text-grey is-size-7">{{
            draft.unit
          }}</span>
          <div class="is-size-7 mt-1">
            <span v-if="onOrderBefore >= ingredient.on_order" class="has-text-success">
              all arrives before plan
            </span>
            <template v-else>
              <span v-if="onOrderBefore > 0" class="has-text-success">
                {{ valueInUnit(onOrderBefore) }} arrives before
              </span>
              <span
                v-if="onOrderAfter > 0"
                class="has-text-warning-dark"
                :class="{ 'ml-1': onOrderBefore > 0 }"
              >
                <template v-if="onOrderBefore > 0">&middot; </template
                >{{ valueInUnit(onOrderAfter) }}
                arrives after
              </span>
            </template>
          </div>
        </template>
        <div
          v-if="draftQtyInBaseUnit > 0"
          class="is-size-7 has-text-info"
          :class="{ 'mt-1': ingredient.on_order > 0 }"
        >
          {{ valueInUnit(draftQtyInBaseUnit) }} on draft
        </div>
        <div
          v-if="stagedQtyBase > 0"
          class="is-size-7 has-text-warning-dark"
          :class="{ 'mt-1': ingredient.on_order > 0 || draftQtyInBaseUnit > 0 }"
        >
          {{ valueInUnit(stagedQtyBase) }} unsaved
        </div>
      </template>
      <span v-else class="has-text-grey-light">—</span>
    </td>

    <td class="no-wrap" data-still-needed>
      <template v-if="adjustedRemainingBase > 0">
        {{ valueInUnit(adjustedRemainingBase) }}&thinsp;<span class="has-text-grey is-size-7">{{
          draft.unit
        }}</span>
      </template>
      <span v-else class="has-text-grey-light">—</span>
    </td>

    <td>
      <div class="field has-addons mb-0">
        <div class="control">
          <input
            type="number"
            class="input is-small po-qty-input"
            min="0"
            step="any"
            :value="pendingQty"
            @input="$emit('update-pending-qty', $event.target.value)"
          />
        </div>
        <div class="control">
          <div class="select is-small">
            <select
              class="po-unit-select"
              :value="draft.unit"
              @change="handleUnitChange($event.target.value)"
            >
              <option v-for="opt in ingredient.unit_options" :key="opt.unit" :value="opt.unit">
                {{ opt.unit }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <p
        v-if="feedbackType"
        class="is-size-7 mt-1 po-feedback"
        :class="feedbackType === 'below' ? 'has-text-warning-dark' : 'has-text-grey-light'"
      >
        Needs: {{ valueInUnit(adjustedRemainingBase) }}&thinsp;{{ draft.unit }}
      </p>
    </td>

    <td>
      <div class="select is-small" style="width: 100%">
        <select
          style="width: 100%"
          data-supplier-select
          :value="draft.supplier_id != null ? String(draft.supplier_id) : ''"
          @change="
            $emit('update-draft', {
              supplier_id: $event.target.value ? +$event.target.value : null,
            })
          "
        >
          <option value="">— No supplier —</option>
          <option v-for="s in ingredient.suppliers" :key="s.id" :value="String(s.id)">
            {{ s.name }}
          </option>
          <optgroup v-if="otherSuppliers.length > 0" label="New supplier">
            <option v-for="s in otherSuppliers" :key="s.id" :value="String(s.id)">
              {{ s.name }}
            </option>
          </optgroup>
        </select>
      </div>
    </td>

    <td class="has-text-centered">
      <button
        class="button is-small"
        :class="needsPurchase ? 'is-primary' : 'is-success is-outlined'"
        :disabled="!canAdd"
        :title="!canAdd ? 'Enter a positive quantity to add to order' : undefined"
        @click="$emit('add', { qty: pendingQty, unit: draft.unit })"
      >
        Add to order
      </button>
    </td>
  </tr>
</template>

<script setup>
  import { computed } from "vue";
  import { toUnit, fmtQty, qtyFeedbackType } from "./unit-utils.js";

  const props = defineProps({
    ingredient: { type: Object, required: true },
    draft: { type: Object, required: true },
    allSuppliers: { type: Array, default: () => [] },
    needsPurchase: { type: Boolean, default: true },
    isSelected: { type: Boolean, default: false },
    linkedDraft: { type: Object, default: null },
    stagedQtyBase: { type: Number, default: 0 },
    pendingQty: { type: String, default: "" },
  });

  const ownSupplierIds = computed(
    () => new Set((props.ingredient.suppliers || []).map((s) => s.id))
  );
  const otherSuppliers = computed(() =>
    props.allSuppliers.filter((s) => !ownSupplierIds.value.has(s.id))
  );

  const emit = defineEmits(["update-draft", "add", "toggle-select", "update-pending-qty"]);

  const valueInUnit = (value) =>
    fmtQty(toUnit(value, props.ingredient.unit, props.draft.unit, props.ingredient.unit_options));

  function handleUnitChange(newUnit) {
    emit("update-draft", { unit: newUnit });
  }

  const feedbackType = computed(() =>
    qtyFeedbackType(
      props.pendingQty,
      toUnit(
        adjustedRemainingBase.value,
        props.ingredient.unit,
        props.draft.unit,
        props.ingredient.unit_options
      )
    )
  );

  const onOrderBefore = computed(() => props.ingredient.on_order_before ?? 0);
  const onOrderAfter = computed(() => Math.max(props.ingredient.on_order - onOrderBefore.value, 0));
  const canAdd = computed(() => parseFloat(props.pendingQty) > 0);

  const draftQtyInBaseUnit = computed(() => {
    if (!props.linkedDraft) return 0;
    const { item } = props.linkedDraft;
    const qty = parseFloat(item.qty);
    if (isNaN(qty) || qty <= 0) return 0;
    return toUnit(qty, item.unit, item.base_unit, item.unit_options);
  });

  const adjustedRemainingBase = computed(() =>
    Math.max(props.ingredient.to_purchase - draftQtyInBaseUnit.value - props.stagedQtyBase, 0)
  );
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .ing-row.in-stock td:not(:first-child) {
    opacity: 0.6;
  }

  .ing-row.in-stock:hover td:not(:first-child) {
    opacity: 0.85;
  }

  td {
    vertical-align: top;
  }

  .po-checkbox-col {
    width: 40px;
    text-align: center;

    input[type="checkbox"] {
      cursor: pointer;
    }
  }

  .po-qty-input {
    width: 84px;
    text-align: right;
    appearance: textfield;
  }

  .po-unit-select {
    width: 72px;
  }
</style>
